import {SelectInput, StatusBadge} from "components/core";
import {ActionButton} from "components/buttons";
import { useFormik } from "formik";
import { FC } from "react";
import { wrapClick, classNames } from "utils";
import {
  IResolveFATServiceOrderFormSchema,
  FactoryAssessmentSchema,
  FactoryAssessmentHeaderMap,
} from "./schema";
import lodash from "lodash";
import {ClipboardDocumentListIcon} from "@heroicons/react/24/outline";
import {FilePreview} from "components/cards";

const fileNameMap = {
  approvedTechnicalDocument: "Approved Technical Document",
  calibrationCertificate: "Calibration Certificate",
  typeTestReport: "Type Test Report",
  ISO14001: "ISO 14001: Environmental Management System Standard",
  ISO45001: "ISO 45001: Occupational Health and Safety Management System",
  ISO9001: "ISO 9001",
  schematicWiringDrawing: "Schematic Wiring Drawing",
  installationOperationalAndMaintenanceManuals: "Installation, Operation and Maintenance Manuals",
  detailedPartListAndMaterialsOfConstruction: "Detailed Parts List and Materials of Construction",
}

export interface FactoryAssessmentFormProps {
  handleNext: (values: IResolveFATServiceOrderFormSchema["factoryAssessment"]) => void;
  handlePrevious: () => void;
  initialValues: IResolveFATServiceOrderFormSchema["factoryAssessment"];
  values: IResolveFATServiceOrderFormSchema;
  handleCancel: () => void;
  meterContractor: any;
}

const FactoryAssessmentForm: FC<FactoryAssessmentFormProps> = ({
  initialValues,
  handleNext,
  handleCancel,
  meterContractor,
  handlePrevious,
}) => {

  const form = useFormik({
    initialValues,
    validationSchema: FactoryAssessmentSchema,
    onSubmit: (values) => {
      handleNext(values);
    },
    onReset: () => {
      handleCancel?.();
    },
  });   

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="space-y-6 divide-y divide-gray-200 p-6 flex-1 overflow-y-auto">
        <div className={"pt-3"}>
          <span className="text-xs font-light">Meter Contractor Documentation</span>
          <div className=" overflow-x-auto mt-2">
            <table className="min-w-full divide-y divide-gray-200 bg-white">
              <thead className="bg-gray-50">
                <tr>
                <th
                  scope="col"
                  className="pl-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                </th>
                <th
                  scope="col"
                  className="pl-3 pr-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Document Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                ></th>
              </tr>
              </thead>

              {/* Table Body */}
              <tbody className="divide-y divide-gray-200">
              {Object.keys(fileNameMap)
                .map((fileKey) => {

                  const doc = lodash.get(meterContractor?.documentation, fileKey);
                  let uploadStatus = !!doc?.url ? "Complete" : "Incomplete"

                  return (
                    <FilePreview
                      url={doc?.url}
                      trigger={
                        ({ setOpen }) => (
                          <tr
                            key={fileKey}
                            className="hover:bg-gray-50 transition-colors"
                            onClick={() => {
                              if(!doc?.url) return
                              setOpen(true)
                            }}
                          >
                            <td className="pl-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900 overflow-ellipsis ">
                              <ClipboardDocumentListIcon className={'text-gray-600 w-4'} />
                            </td>
                            <td className="pl-3 pr-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 overflow-ellipsis ">
                              <p className={"max-w-[250px] overflow-hidden text-ellipsis"}>
                                {fileNameMap[fileKey as keyof typeof fileNameMap]}
                              </p>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge
                                status={uploadStatus}
                                options={{
                                  "Complete": "bg-green-100 text-green-600",
                                  "Incomplete": "bg-red-100 text-red-600",
                                }}
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <ActionButton
                                  action={'view'}
                                  onClick={() => {}}
                                />
                              </div>
                            </td>
                          </tr>
                        )
                      }
                    />
                  )})}
              </tbody>
            </table>
          </div>
        </div>
        <div className={"pt-6"}>
          <span className="text-xs font-light">Factory Assessment</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            <div className="col-span-3">
              <div className="mt-1 w-full ">
                <table className="min-w-full divide-y divide-gray-300 border border-gray-300 rounded-md">
                  <thead className="bg-gray-50">
                    <tr className="divide-x divide-gray-200">
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-2 text-left text-sm font-semibold text-gray-900"
                      >
                        Criteria
                      </th>
                      <th
                        scope="col"
                        className="w-1/2 whitespace-nowrap px-2 py-2 text-sm font-semibold text-gray-900"
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                  {lodash.map(
                    Object.keys(FactoryAssessmentHeaderMap),
                    (header, idx) => {
                      const id = `${header}`;

                      return (
                        <tr key={idx} className="divide-x divide-gray-200">
                          <td className="p-0">
                            <input
                              key={idx}
                              type={"text"}
                              id={`index`}
                              name={`index`}
                              value={`${FactoryAssessmentHeaderMap[header as keyof  typeof FactoryAssessmentHeaderMap]}`}
                              placeholder={"eg. 1"}
                              disabled
                              className={classNames(
                                "focus:ring-primary-500 focus:border-primary-500 border-gray-200 bg-gray-50",
                                "block w-full sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                              )}
                            />
                          </td>
                          <td className="p-0 whitespace-nowrap w-1/2 text-sm text-gray-500 dark:text-gray-400 text-right w-[200px] h-[38px]">
                            <SelectInput
                              id={`${id}`}
                              labelHidden
                              errorsHidden
                              options={[
                                { label: "--- Select Status ---", value: "" },
                                { label: "Poor", value: "Poor" },
                                { label: "Average", value: "Average" },
                                { label: "Good", value: "Good" },
                                { label: "Very Good", value: "VeryGood" },
                                { label: "Excellent", value: "Excellent" },
                              ]}
                              {...form}
                            />
                          </td>
                        </tr>
                      );
                    }
                  )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
        <button
          type="button"
          data-invalid={!form.isValid}
          onClick={wrapClick(form.handleSubmit)}
          className="w-full inline-flex justify-center data-invalid:bg-gray-200 data-invalid:border-gray-400 data-invalid:cursor-not-allowed data-invalid:text-gray-600 rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Next
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(form.resetForm)}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={wrapClick(handlePrevious)}
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Previous
        </button>   
      </div>
    </div>
  );
};

export default FactoryAssessmentForm;
